/**
 * Admin console (/admin) — FEA-30, docs/FRONTEND-PLAN.md §4.3.
 *
 * Route is admin-only (ProtectedRoute's `adminOnly`, App.jsx) matching the
 * backend's `requireAdmin` short-circuit on GET/PUT /admin/site-settings and
 * the hard-coded admin gate on GET /users/search.
 *
 * Two tabs:
 *  - Settings: whole-object form over the admin GET/PUT site-settings pair,
 *    with a live preview using the exact AnnouncementBanner component the
 *    real site renders.
 *  - Communities: forum category list with moderator chips, add/remove
 *    writing the FULL moderators array via PUT /forum/categories/{id}, and
 *    the existing CategoryForm (FEA-19) reused for full category edits.
 */
import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../api/admin';
import * as forumApi from '../api/forum';
import { useSiteSettings } from '../lib/siteSettings';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import Tabs from '../components/Tabs';
import AnnouncementBanner from '../components/AnnouncementBanner';
import CategoryForm, { categoryToForm } from '../components/CategoryForm';
import {
  addRole,
  hasWildcard,
  isChecked,
  isRequiredRole,
  removeRole,
  roleMapErrors,
  setLabel,
  toggleValue,
} from '../lib/roleEditor';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  TextareaField,
  SelectField,
  UserChip,
  formatDateTime,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'communities', label: 'Communities' },
  { id: 'roles', label: 'Roles' },
];

const FEATURE_FIELDS = [
  ['forum', 'Forum'],
  ['events', 'Events'],
  ['calendar', 'Calendar'],
  ['tasks', 'Tasks'],
];

function emptySettingsForm() {
  return {
    announcement: { enabled: false, message: '', tone: 'info' },
    supportEmail: '',
    registrationOpen: true,
    features: { forum: true, events: true, calendar: true, tasks: true },
  };
}

function settingsToForm(settings) {
  const announcement = settings.announcement || {};
  const features = settings.features || {};
  return {
    announcement: {
      enabled: announcement.enabled === true,
      message: announcement.message || '',
      tone: announcement.tone === 'warning' ? 'warning' : 'info',
    },
    supportEmail: settings.supportEmail || '',
    registrationOpen: settings.registrationOpen !== false,
    features: {
      forum: features.forum !== false,
      events: features.events !== false,
      calendar: features.calendar !== false,
      tasks: features.tasks !== false,
    },
  };
}

/** Whole-object PUT body — every top-level section, every time. */
function buildSettingsPayload(form) {
  return {
    announcement: {
      enabled: form.announcement.enabled === true,
      message: form.announcement.message.trim(),
      tone: form.announcement.tone,
    },
    supportEmail: form.supportEmail.trim() || null,
    registrationOpen: form.registrationOpen === true,
    features: { ...form.features },
  };
}

function SettingsPanel() {
  const { refresh: refreshPublicSettings } = useSiteSettings();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [record, setRecord] = useState(null); // raw admin record incl. updatedAt/updatedBy
  const [form, setForm] = useState(emptySettingsForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    adminApi
      .getSiteSettings()
      .then(data => {
        setRecord(data.settings);
        setForm(settingsToForm(data.settings));
      })
      .catch(err => setLoadError(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = patch => setForm(current => ({ ...current, ...patch }));
  const setAnnouncement = patch =>
    setForm(current => ({ ...current, announcement: { ...current.announcement, ...patch } }));
  const setFeature = (key, value) =>
    setForm(current => ({ ...current, features: { ...current.features, [key]: value } }));

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const { settings } = await adminApi.putSiteSettings(buildSettingsPayload(form));
      setRecord(settings);
      setForm(settingsToForm(settings));
      toast.success('Site settings saved.');
      // Re-fetch the public copy so nav/banner/footer/Register reflect the
      // change immediately, without waiting on the next visibilitychange.
      refreshPublicSettings();
    } catch (err) {
      setSaveError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner />;
  if (loadError) {
    return (
      <div className={ui.card}>
        <ErrorAlert error={loadError} />
        <button type="button" className={ui.btnSecondary} onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSave} className={ui.card}>
      <ErrorAlert error={saveError} />

      <fieldset>
        <legend className={ui.label}>Site-wide announcement</legend>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="checkbox"
            checked={form.announcement.enabled}
            onChange={e => setAnnouncement({ enabled: e.target.checked })}
          />{' '}
          Show an announcement banner to every member
        </label>
        <TextareaField
          label="Message"
          rows={3}
          maxLength={500}
          hint={`${form.announcement.message.length}/500 characters`}
          value={form.announcement.message}
          onChange={e => setAnnouncement({ message: e.target.value })}
        />
        <SelectField
          label="Tone"
          value={form.announcement.tone}
          onChange={e => setAnnouncement({ tone: e.target.value })}
        >
          <option value="info">Info</option>
          <option value="warning">Warning</option>
        </SelectField>
        <p className={cn(ui.label, 'mt-4')}>Preview</p>
        {form.announcement.enabled && form.announcement.message ? (
          <div className={cn(ui.card, 'overflow-hidden p-0!')}>
            <AnnouncementBanner
              enabled={form.announcement.enabled}
              message={form.announcement.message}
              tone={form.announcement.tone}
            />
          </div>
        ) : (
          <p className={cn(ui.muted, 'text-body-s')}>
            Enable it and add a message to preview the banner.
          </p>
        )}
      </fieldset>

      <fieldset className="mt-4">
        <legend className={ui.label}>Support</legend>
        <Field
          label="Support email (optional)"
          type="email"
          hint="Shown in the site footer and on the registration-closed notice."
          value={form.supportEmail}
          onChange={e => set({ supportEmail: e.target.value })}
        />
      </fieldset>

      <fieldset className="mt-4">
        <legend className={ui.label}>Registration</legend>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="checkbox"
            checked={form.registrationOpen}
            onChange={e => set({ registrationOpen: e.target.checked })}
          />{' '}
          New member registration is open
        </label>
      </fieldset>

      <fieldset className="mt-4">
        <legend className={ui.label}>Features</legend>
        <p className={cn(ui.muted, 'text-body-s')}>
          Turning a feature off hides its nav item and blocks its pages for every member — you (as
          an admin) still see and can use everything, so you can judge the effect before confirming
          it.
        </p>
        {FEATURE_FIELDS.map(([key, label]) => (
          <label key={key} className={cn(ui.row, 'text-body-s')}>
            <input
              type="checkbox"
              checked={form.features[key]}
              onChange={e => setFeature(key, e.target.checked)}
            />{' '}
            {label}
          </label>
        ))}
      </fieldset>

      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      {record && (record.updatedAt || record.updatedBy) && (
        <p className={cn(ui.muted, 'text-body-s mt-4')}>
          Last updated {record.updatedAt ? formatDateTime(record.updatedAt) : 'never'}
          {record.updatedBy && (
            <>
              {' '}
              by <UserChip userId={record.updatedBy} />
            </>
          )}
        </p>
      )}
    </form>
  );
}

/** Search-by-email-prefix picker rendered inside the "Add moderator" modal. */
function UserSearchPicker({ onPick }) {
  const [emailPrefix, setEmailPrefix] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  async function onSearch(event) {
    event.preventDefault();
    const prefix = emailPrefix.trim();
    if (!prefix) return;
    setSearching(true);
    setError(null);
    try {
      const { users } = await adminApi.searchUsers({ emailPrefix: prefix, limit: 20 });
      setResults(users || []);
    } catch (err) {
      setError(err);
    } finally {
      setSearching(false);
    }
  }

  function displayName(u) {
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return name || u.username || u.email;
  }

  return (
    <>
      <ErrorAlert error={error} />
      <form onSubmit={onSearch} className={cn(ui.row, 'items-end')}>
        <Field
          label="Search by email prefix"
          placeholder="name@example.com"
          value={emailPrefix}
          onChange={e => setEmailPrefix(e.target.value)}
        />
        <button type="submit" className={ui.btnPrimary} disabled={searching || !emailPrefix.trim()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      {results === null ? (
        <EmptyState>Search by email prefix to find a member to designate.</EmptyState>
      ) : results.length === 0 ? (
        <EmptyState>No members match that email prefix.</EmptyState>
      ) : (
        <ul className="m-0 list-none p-0">
          {results.map(u => (
            <li key={u.userId} className="mb-4">
              <button
                type="button"
                className={cn(ui.btnSecondary, 'w-full justify-start!')}
                onClick={() => onPick(u)}
              >
                {displayName(u)} — {u.email} ({u.role})
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function CommunitiesPanel() {
  const confirm = useConfirm();
  const toast = useToast();

  const [categories, setCategories] = useState(null);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null); // `${categoryId}:${userId}`
  const [editingCategory, setEditingCategory] = useState(null);
  const [addingTo, setAddingTo] = useState(null); // category object, or null
  // Freshly-added moderators show a real name immediately (from the search
  // result that added them) instead of the bare userId UserChip falls back
  // to — pre-existing moderators have no denormalised name anywhere the
  // client can reach (GET /users/{userId} for another user 403s under the
  // FND-43 permission gap; only /users/search returns a name, and that
  // needs an email prefix, not a bare id) and show as their userId.
  const [nameCache, setNameCache] = useState({});

  const load = useCallback(() => {
    forumApi
      .listCategories()
      .then(d => setCategories(d.categories || []))
      .catch(err => setError(err));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function removeModerator(category, userId) {
    const ok = await confirm({
      title: 'Remove moderator?',
      message: `Remove this member's moderator powers in "${category.name}"?`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const key = `${category.categoryId}:${userId}`;
    setBusyKey(key);
    try {
      const nextModerators = (category.moderators || []).filter(id => id !== userId);
      const { category: updated } = await forumApi.updateCategory(category.categoryId, {
        moderators: nextModerators,
      });
      setCategories(current =>
        current.map(c => (c.categoryId === updated.categoryId ? updated : c))
      );
      toast.success('Moderator removed.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyKey(null);
    }
  }

  async function addModerator(category, candidate) {
    const current = category.moderators || [];
    if (current.includes(candidate.userId)) {
      toast.error('That member is already a moderator of this category.');
      return;
    }
    try {
      const { category: updated } = await forumApi.updateCategory(category.categoryId, {
        moderators: [...current, candidate.userId],
      });
      setCategories(cur => cur.map(c => (c.categoryId === updated.categoryId ? updated : c)));
      const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
      setNameCache(cur => ({
        ...cur,
        [candidate.userId]: name || candidate.username || candidate.email,
      }));
      toast.success('Moderator added.');
      setAddingTo(null);
    } catch (err) {
      toast.error(err);
    }
  }

  async function saveCategoryEdit(payload) {
    if (!editingCategory) return;
    const { category } = await forumApi.updateCategory(editingCategory.categoryId, payload);
    setCategories(cur => cur.map(c => (c.categoryId === category.categoryId ? category : c)));
    toast.success(`Category "${category.name}" updated.`);
    setEditingCategory(null);
  }

  if (error) return <ErrorAlert error={error} />;
  if (categories === null) return <Spinner />;
  if (categories.length === 0) return <EmptyState>No forum categories yet.</EmptyState>;

  return (
    <>
      {categories.map(c => (
        <section key={c.categoryId} className={cn(ui.card, 'mb-4')}>
          <div className={cn(ui.row, ui.spread, 'items-start')}>
            <div>
              <h3 className="text-title-s">
                {c.name}
                {!c.isActive && <span className={ui.pillNeutral}> Inactive</span>}
              </h3>
              {c.description && <p className={cn(ui.muted, 'text-body-s')}>{c.description}</p>}
            </div>
            <button type="button" className={ui.btnSecondary} onClick={() => setEditingCategory(c)}>
              Edit category
            </button>
          </div>

          <p className={cn(ui.label, 'mt-4')}>Moderators</p>
          <div className={ui.row}>
            {(c.moderators || []).length === 0 && (
              <span className={cn(ui.muted, 'text-body-s')}>No moderators designated.</span>
            )}
            {(c.moderators || []).map(userId => (
              <span key={userId} className="flex flex-wrap items-center gap-1 *:min-w-0">
                <UserChip userId={userId} name={nameCache[userId]} />
                <button
                  type="button"
                  className={ui.btnSecondary}
                  aria-label={`Remove moderator ${nameCache[userId] || userId}`}
                  disabled={busyKey === `${c.categoryId}:${userId}`}
                  onClick={() => removeModerator(c, userId)}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>

          <div className={cn(ui.row, 'mt-4')}>
            <button type="button" className={ui.btnSecondary} onClick={() => setAddingTo(c)}>
              Add moderator
            </button>
          </div>
        </section>
      ))}

      <Modal
        open={Boolean(editingCategory)}
        onClose={() => setEditingCategory(null)}
        title={editingCategory ? `Edit "${editingCategory.name}"` : 'Edit category'}
      >
        {editingCategory && (
          <CategoryForm
            key={editingCategory.categoryId}
            initial={categoryToForm(editingCategory)}
            submitLabel="Save changes"
            showIsActive
            onSubmit={saveCategoryEdit}
            onCancel={() => setEditingCategory(null)}
          />
        )}
      </Modal>

      <Modal
        open={Boolean(addingTo)}
        onClose={() => setAddingTo(null)}
        title={addingTo ? `Add moderator to "${addingTo.name}"` : 'Add moderator'}
      >
        {addingTo && (
          <UserSearchPicker
            key={addingTo.categoryId}
            onPick={candidate => addModerator(addingTo, candidate)}
          />
        )}
      </Modal>
    </>
  );
}

/**
 * Roles panel (FEA-34): edit which capabilities and modules each role carries.
 *
 * The server is the gate — it re-validates everything below and 400s — but the
 * console refuses to submit a map that would lock administration out, because
 * discovering that by submitting it is a bad way to find out.
 */
function RolesPanel() {
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [meta, setMeta] = useState(null); // vocabularies + updatedAt/updatedBy
  const [roles, setRoles] = useState({});
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    adminApi
      .getRoles()
      .then(data => {
        setRoles(data.roles || {});
        setMeta(data);
      })
      .catch(err => setLoadError(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requiredRoles = (meta && meta.requiredRoles) || [];
  const capabilities = (meta && meta.availableCapabilities) || [];
  const modules = (meta && meta.availableModules) || [];
  const blockers = roleMapErrors(roles, requiredRoles);

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const data = await adminApi.putRoles(roles);
      setRoles(data.roles || {});
      setMeta(data);
      toast.success('Roles saved. Changes take effect within a minute.');
    } catch (err) {
      setSaveError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function onRemove(name) {
    const ok = await confirm({
      title: `Delete the ${name} role?`,
      message:
        'Accounts still holding this role keep the role name but lose every capability and module it granted, ' +
        'so they will be refused anywhere it was their only route in. This takes effect when you save.',
      confirmLabel: 'Delete role',
      danger: true,
    });
    if (ok) setRoles(current => removeRole(current, name));
  }

  if (loading) return <Spinner />;
  if (loadError) {
    return (
      <div className={ui.card}>
        <ErrorAlert error={loadError} />
        <button type="button" className={ui.btnGhost} onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSave}>
      <div className={cn(ui.card, 'mb-4')}>
        <p className={ui.muted}>
          A capability is permission to perform an operation; a module is permission to open an area
          at all. Administration is the <code>system:admin</code> capability, not the name
          &ldquo;admin&rdquo; — a new role granted it can administer the platform.
        </p>
        {meta && meta.isDefault ? (
          <p className={ui.muted}>Running on the built-in defaults — nothing has been saved yet.</p>
        ) : meta && meta.updatedAt ? (
          <p className={ui.muted}>Last saved {formatDateTime(meta.updatedAt)}</p>
        ) : null}
      </div>

      {blockers.length ? (
        <div className={cn(ui.card, 'mb-4')} role="alert">
          <ul className="mb-0">
            {blockers.map(message => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {saveError ? <ErrorAlert error={saveError} /> : null}

      {Object.keys(roles)
        .sort()
        .map(name => {
          const definition = roles[name];
          const required = isRequiredRole(name, requiredRoles);
          return (
            <section key={name} className={cn(ui.card, 'mb-4')}>
              <div className={cn(ui.row, ui.spread, 'mb-2')}>
                <h3 className="text-title-s">
                  <code>{name}</code>
                  {required ? <span className="ml-2">required</span> : null}
                </h3>
                {required ? null : (
                  <button type="button" className={ui.btnDanger} onClick={() => onRemove(name)}>
                    Delete role
                  </button>
                )}
              </div>

              <Field
                label="Display name"
                value={definition.label || ''}
                onChange={e => setRoles(current => setLabel(current, name, e.target.value))}
              />

              {[
                ['capabilities', capabilities, 'Capabilities'],
                ['modules', modules, 'Modules'],
              ].map(([field, options, legend]) => (
                <fieldset key={field} className="mt-3">
                  <legend>{legend}</legend>
                  {hasWildcard(definition, field) ? (
                    <p className={ui.muted}>
                      Holds every {field === 'capabilities' ? 'capability' : 'module'} (
                      <code>*</code>).
                    </p>
                  ) : (
                    <div className={ui.grid}>
                      {options.map(option => (
                        <label key={option} className={ui.row}>
                          <input
                            type="checkbox"
                            checked={isChecked(definition, field, option)}
                            onChange={() =>
                              setRoles(current => toggleValue(current, name, field, option))
                            }
                          />
                          <code>{option}</code>
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
              ))}
            </section>
          );
        })}

      <div className={cn(ui.card, 'mb-4')}>
        <Field
          label="New role name"
          value={newRole}
          onChange={e => setNewRole(e.target.value)}
          hint="Lower-case, hyphenated. Spaces become hyphens."
        />
        <button
          type="button"
          className={ui.btnGhost}
          disabled={!newRole.trim()}
          onClick={() => {
            setRoles(current => addRole(current, newRole));
            setNewRole('');
          }}
        >
          Add role
        </button>
      </div>

      <button type="submit" className={ui.btnPrimary} disabled={saving || blockers.length > 0}>
        {saving ? 'Saving…' : 'Save roles'}
      </button>
    </form>
  );
}

export default function Admin() {
  const [tab, setTab] = useState('settings');

  return (
    <>
      <h1 className={ui.h1}>Admin console</h1>
      <Tabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
        idPrefix="admin"
        aria-label="Admin sections"
      />
      <div
        role="tabpanel"
        id={`admin-panel-${tab}`}
        aria-labelledby={`admin-${tab}`}
        className="mt-4"
      >
        {tab === 'settings' ? <SettingsPanel /> : null}
        {tab === 'communities' ? <CommunitiesPanel /> : null}
        {tab === 'roles' ? <RolesPanel /> : null}
      </div>
    </>
  );
}
